import { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Compass, Music, Film, Tv, Globe, Users, User, Search, Loader2, Download, Disc3, Sparkles, ChevronRight, ChevronDown, Youtube, CheckCircle, AlertCircle, RefreshCw, HardDrive } from 'lucide-react'
import { libraries, artists, albums, importer } from '../api/client'
import type { YtSearchResult, DabSearchResult, HifiSearchResult, ProwlarrVideoResult, VideoSyncStatus } from '../api/client'

type SourceFilter = 'all' | 'global' | 'shared'
type MediaTab = 'music' | 'movies' | 'tv' | 'find-music' | 'youtube'
type MusicSubTab = 'dab' | 'monochrome' | 'squid'

export default function Discover() {
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all')
  const [activeTab, setActiveTab] = useState<MediaTab>('music')
  const [showAllArtists, setShowAllArtists] = useState(false)
  const [showAllAlbums, setShowAllAlbums] = useState(false)
  const [showSyncedMovies, setShowSyncedMovies] = useState(false)
  const [showSyncedTv, setShowSyncedTv] = useState(false)

  // Get all libraries to filter content
  const { data: libraryList } = useQuery({
    queryKey: ['libraries'],
    queryFn: libraries.list,
  })

  // Fetch content (use browse endpoints to get all content, not just user's collection)
  const { data: allArtists, isLoading: artistsLoading } = useQuery({
    queryKey: ['artists', 'browse'],
    queryFn: () => artists.browse(500, 0),
  })

  const { data: allAlbums, isLoading: albumsLoading } = useQuery({
    queryKey: ['albums', 'browse'],
    queryFn: () => albums.browse(200, 0),
  })

  // Get library IDs by access level
  // Use is_global flag instead of access_level to properly detect global libraries
  // (access_level is 'owner' for the owner even if the library is global)
  // Handle is_global as either boolean or integer (SQLite returns 0/1)
  const globalLibraryIds = useMemo(() => {
    if (!libraryList) return new Set<string>()
    return new Set(
      libraryList
        .filter((lib) => lib.is_global === true || (lib.is_global as unknown) === 1)
        .map((lib) => lib.id)
    )
  }, [libraryList])

  const sharedLibraryIds = useMemo(() => {
    if (!libraryList) return new Set<string>()
    return new Set(
      libraryList
        .filter((lib) => lib.access_level === 'shared')
        .map((lib) => lib.id)
    )
  }, [libraryList])

  const discoverableLibraryIds = useMemo(() => {
    return new Set([...globalLibraryIds, ...sharedLibraryIds])
  }, [globalLibraryIds, sharedLibraryIds])

  // Filter content based on source filter
  const getFilteredLibraryIds = () => {
    if (sourceFilter === 'global') return globalLibraryIds
    if (sourceFilter === 'shared') return sharedLibraryIds
    return discoverableLibraryIds
  }

  const filteredLibraryIds = getFilteredLibraryIds()

  // Filtered albums - show all albums from browse endpoint
  // The browse endpoint already returns all albums the user can access
  // No need to filter further since Discover is meant to show all available content
  const filteredAlbums = useMemo(() => {
    if (!allAlbums) return []
    // Just return all albums - the browse endpoint handles access control
    return allAlbums
  }, [allAlbums])

  // Debug logging for filtering issues
  useEffect(() => {
    console.log('Discover Debug:', {
      libraryList: libraryList?.map(l => ({ id: l.id, name: l.name, is_global: l.is_global, access_level: l.access_level })),
      globalLibraryIds: Array.from(globalLibraryIds),
      filteredLibraryIdsCount: filteredLibraryIds.size,
      allAlbumsCount: allAlbums?.length,
      filteredAlbumsCount: filteredAlbums.length,
      allArtistsCount: allArtists?.length,
      sampleAlbum: allAlbums?.[0],
    })
  }, [libraryList, globalLibraryIds, filteredLibraryIds, allAlbums, filteredAlbums, allArtists])

  // Get artists - just show all artists from browse endpoint
  // The browse endpoint already returns all artists the user can access
  const filteredArtists = useMemo(() => {
    if (!allArtists) return []
    return allArtists
  }, [allArtists])

  // Count discoverable content (handle is_global as boolean or integer)
  const globalCount = libraryList?.filter((lib) => lib.is_global === true || (lib.is_global as unknown) === 1).length || 0
  const sharedCount = libraryList?.filter((lib) => lib.access_level === 'shared').length || 0

  // Music sub-tab state (for Find New Music)
  const [musicSubTab, setMusicSubTab] = useState<MusicSubTab>('dab')

  // DAB Search state
  const [dabQuery, setDabQuery] = useState('')
  const [dabSearchTerm, setDabSearchTerm] = useState('')
  const [dabDownloadingIds, setDabDownloadingIds] = useState<Set<string>>(new Set())
  const [dabDownloadedIds, setDabDownloadedIds] = useState<Set<string>>(new Set())
  const [dabDownloadErrors, setDabDownloadErrors] = useState<Record<string, string>>({})

  // Monochrome Search state
  const [monoQuery, setMonoQuery] = useState('')
  const [monoSearchTerm, setMonoSearchTerm] = useState('')
  const [monoDownloadingIds, setMonoDownloadingIds] = useState<Set<string>>(new Set())
  const [monoDownloadedIds, setMonoDownloadedIds] = useState<Set<string>>(new Set())
  const [monoDownloadErrors, setMonoDownloadErrors] = useState<Record<string, string>>({})

  // Squid Search state
  const [squidQuery, setSquidQuery] = useState('')
  const [squidSearchTerm, setSquidSearchTerm] = useState('')
  const [squidDownloadingIds, setSquidDownloadingIds] = useState<Set<string>>(new Set())
  const [squidDownloadedIds, setSquidDownloadedIds] = useState<Set<string>>(new Set())
  const [squidDownloadErrors, setSquidDownloadErrors] = useState<Record<string, string>>({})

  // Prowlarr Movies search state
  const [movieQuery, setMovieQuery] = useState('')
  const [movieSearchTerm, setMovieSearchTerm] = useState('')
  const [movieFilter1080p, setMovieFilter1080p] = useState(true)
  const [movieDownloadingGuids, setMovieDownloadingGuids] = useState<Set<string>>(new Set())
  const [movieDownloadedGuids, setMovieDownloadedGuids] = useState<Set<string>>(new Set())
  const [movieDownloadErrors, setMovieDownloadErrors] = useState<Record<string, string>>({})

  // Prowlarr TV search state
  const [tvQuery, setTvQuery] = useState('')
  const [tvSearchTerm, setTvSearchTerm] = useState('')
  const [tvFilter1080p, setTvFilter1080p] = useState(true)
  const [tvDownloadingGuids, setTvDownloadingGuids] = useState<Set<string>>(new Set())
  const [tvDownloadedGuids, setTvDownloadedGuids] = useState<Set<string>>(new Set())
  const [tvDownloadErrors, setTvDownloadErrors] = useState<Record<string, string>>({})

  // Track which torrent hash is currently being synced (for UI state)
  const [syncingHash, setSyncingHash] = useState<string | null>(null)

  // Video sync status - polls for download/sync progress
  // Poll every 2 seconds when syncing (syncingHash is set), 10 seconds otherwise
  const { data: videoSyncStatus, refetch: refetchVideoSync, error: videoSyncError } = useQuery({
    queryKey: ['video-sync-status'],
    queryFn: importer.getVideoSyncStatus,
    refetchInterval: (query) => {
      // Poll faster when we know we're syncing
      if (syncingHash !== null) return 2000
      // Also poll faster if the data shows something is syncing
      const data = query.state.data
      if (data && data.some((s: VideoSyncStatus) => s.syncing && s.syncing.length > 0)) return 2000
      return 10000
    },
    enabled: activeTab === 'movies' || activeTab === 'tv',
  })

  // Debug logging for video sync status
  useEffect(() => {
    console.log('Video Sync Status Debug:', {
      activeTab,
      videoSyncStatus,
      videoSyncError,
    })
  }, [activeTab, videoSyncStatus, videoSyncError])

  // Get sync status for movies and TV
  const movieSyncStatus = useMemo(() => {
    const status = videoSyncStatus?.find((s: VideoSyncStatus) => s.label === 'movies')
    console.log('Movie Sync Status:', status)
    return status
  }, [videoSyncStatus])
  const tvSyncStatus = useMemo(() => {
    const status = videoSyncStatus?.find((s: VideoSyncStatus) => s.label === 'tv')
    console.log('TV Sync Status:', status)
    return status
  }, [videoSyncStatus])

  // Sync mutation for videos
  const syncVideoMutation = useMutation({
    mutationFn: async ({ label, hash }: { label: 'movies' | 'tv', hash?: string }) => {
      console.log('Starting sync:', { label, hash })
      setSyncingHash(hash || 'all')
      return importer.syncVideoTorrents(label, hash)
    },
    onSuccess: (data) => {
      console.log('Sync success:', data)
      setSyncingHash(null)
      refetchVideoSync()
    },
    onError: (error) => {
      console.error('Sync error:', error)
      setSyncingHash(null)
      alert(`Sync failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    },
  })

  // DAB search query
  const { data: dabResults, isLoading: dabSearching } = useQuery({
    queryKey: ['dab-search', dabSearchTerm],
    queryFn: () => importer.searchDab(dabSearchTerm),
    enabled: dabSearchTerm.length > 2,
  })

  // Monochrome search query
  const { data: monoResults, isLoading: monoSearching } = useQuery({
    queryKey: ['monochrome-search', monoSearchTerm],
    queryFn: () => importer.searchMonochrome(monoSearchTerm),
    enabled: monoSearchTerm.length > 2,
  })

  // Squid search query
  const { data: squidResults, isLoading: squidSearching } = useQuery({
    queryKey: ['squid-search', squidSearchTerm],
    queryFn: () => importer.searchSquid(squidSearchTerm),
    enabled: squidSearchTerm.length > 2,
  })

  // Helper to normalize strings for comparison (removes case, extra spaces, punctuation)
  const normalizeForComparison = (s: string): string => {
    return s
      .toLowerCase()
      .replace(/[^\w\s]/g, '') // Remove punctuation
      .replace(/\s+/g, ' ')    // Normalize spaces
      .trim()
  }

  // Check which DAB results already exist in the library
  const existingAlbumsMap = useMemo(() => {
    if (!allAlbums) return new Map<string, string>()
    const map = new Map<string, string>() // key: normalized "artist|album", value: album id
    for (const album of allAlbums) {
      const key = `${normalizeForComparison(album.artist_name || '')}|${normalizeForComparison(album.title)}`
      map.set(key, album.id)
    }
    return map
  }, [allAlbums])

  // Check if a DAB result already exists in the library
  const getExistingAlbumId = (result: DabSearchResult): string | null => {
    const key = `${normalizeForComparison(result.artist)}|${normalizeForComparison(result.title)}`
    return existingAlbumsMap.get(key) || null
  }

  const handleDabSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (dabQuery.trim().length > 2) {
      setDabSearchTerm(dabQuery.trim())
      // Clear previous download states
      setDabDownloadingIds(new Set())
      setDabDownloadedIds(new Set())
      setDabDownloadErrors({})
    }
  }

  // DAB download mutation
  const downloadDabMutation = useMutation({
    mutationFn: async (result: DabSearchResult) => {
      return importer.downloadDabStandalone(result.album_id, result.artist, result.title)
    },
    onMutate: (result) => {
      setDabDownloadingIds(prev => new Set(prev).add(result.album_id))
      setDabDownloadErrors(prev => {
        const next = { ...prev }
        delete next[result.album_id]
        return next
      })
    },
    onSuccess: (data, result) => {
      setDabDownloadingIds(prev => {
        const next = new Set(prev)
        next.delete(result.album_id)
        return next
      })
      if (data.success) {
        setDabDownloadedIds(prev => new Set(prev).add(result.album_id))
        // Invalidate library queries to show new albums
        queryClient.invalidateQueries({ queryKey: ['tracks'] })
        queryClient.invalidateQueries({ queryKey: ['artists'] })
        queryClient.invalidateQueries({ queryKey: ['albums'] })
      } else {
        setDabDownloadErrors(prev => ({
          ...prev,
          [result.album_id]: data.message || 'Download failed'
        }))
      }
    },
    onError: (error: unknown, result) => {
      setDabDownloadingIds(prev => {
        const next = new Set(prev)
        next.delete(result.album_id)
        return next
      })
      // Extract error message from axios response or fallback to generic error
      let errorMsg = 'Download failed'
      if (error && typeof error === 'object') {
        const axiosError = error as { response?: { data?: string }; message?: string }
        errorMsg = axiosError.response?.data || axiosError.message || 'Download failed'
      }
      setDabDownloadErrors(prev => ({
        ...prev,
        [result.album_id]: errorMsg
      }))
    },
  })

  // Monochrome search handler
  const handleMonoSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (monoQuery.trim().length > 2) {
      setMonoSearchTerm(monoQuery.trim())
      setMonoDownloadingIds(new Set())
      setMonoDownloadedIds(new Set())
      setMonoDownloadErrors({})
    }
  }

  // Monochrome download mutation
  const downloadMonoMutation = useMutation({
    mutationFn: async (result: HifiSearchResult) => {
      return importer.downloadMonochromeStandalone(result.album_id, result.artist, result.title, 'LOSSLESS')
    },
    onMutate: (result) => {
      setMonoDownloadingIds(prev => new Set(prev).add(result.album_id))
      setMonoDownloadErrors(prev => {
        const next = { ...prev }
        delete next[result.album_id]
        return next
      })
    },
    onSuccess: (data, result) => {
      setMonoDownloadingIds(prev => {
        const next = new Set(prev)
        next.delete(result.album_id)
        return next
      })
      if (data.success) {
        setMonoDownloadedIds(prev => new Set(prev).add(result.album_id))
        queryClient.invalidateQueries({ queryKey: ['tracks'] })
        queryClient.invalidateQueries({ queryKey: ['artists'] })
        queryClient.invalidateQueries({ queryKey: ['albums'] })
      } else {
        setMonoDownloadErrors(prev => ({
          ...prev,
          [result.album_id]: data.message || 'Download failed'
        }))
      }
    },
    onError: (error: unknown, result) => {
      setMonoDownloadingIds(prev => {
        const next = new Set(prev)
        next.delete(result.album_id)
        return next
      })
      let errorMsg = 'Download failed'
      if (error && typeof error === 'object') {
        const axiosError = error as { response?: { data?: string }; message?: string }
        errorMsg = axiosError.response?.data || axiosError.message || 'Download failed'
      }
      setMonoDownloadErrors(prev => ({
        ...prev,
        [result.album_id]: errorMsg
      }))
    },
  })

  // Squid search handler
  const handleSquidSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (squidQuery.trim().length > 2) {
      setSquidSearchTerm(squidQuery.trim())
      setSquidDownloadingIds(new Set())
      setSquidDownloadedIds(new Set())
      setSquidDownloadErrors({})
    }
  }

  // Squid download mutation
  const downloadSquidMutation = useMutation({
    mutationFn: async (result: HifiSearchResult) => {
      return importer.downloadSquidStandalone(result.album_id, result.artist, result.title, 'LOSSLESS')
    },
    onMutate: (result) => {
      setSquidDownloadingIds(prev => new Set(prev).add(result.album_id))
      setSquidDownloadErrors(prev => {
        const next = { ...prev }
        delete next[result.album_id]
        return next
      })
    },
    onSuccess: (data, result) => {
      setSquidDownloadingIds(prev => {
        const next = new Set(prev)
        next.delete(result.album_id)
        return next
      })
      if (data.success) {
        setSquidDownloadedIds(prev => new Set(prev).add(result.album_id))
        queryClient.invalidateQueries({ queryKey: ['tracks'] })
        queryClient.invalidateQueries({ queryKey: ['artists'] })
        queryClient.invalidateQueries({ queryKey: ['albums'] })
      } else {
        setSquidDownloadErrors(prev => ({
          ...prev,
          [result.album_id]: data.message || 'Download failed'
        }))
      }
    },
    onError: (error: unknown, result) => {
      setSquidDownloadingIds(prev => {
        const next = new Set(prev)
        next.delete(result.album_id)
        return next
      })
      let errorMsg = 'Download failed'
      if (error && typeof error === 'object') {
        const axiosError = error as { response?: { data?: string }; message?: string }
        errorMsg = axiosError.response?.data || axiosError.message || 'Download failed'
      }
      setSquidDownloadErrors(prev => ({
        ...prev,
        [result.album_id]: errorMsg
      }))
    },
  })

  // Prowlarr Movies search query
  const { data: movieResults, isLoading: movieSearching } = useQuery({
    queryKey: ['prowlarr-movies', movieSearchTerm],
    queryFn: () => importer.searchProwlarrMovies(movieSearchTerm),
    enabled: movieSearchTerm.length > 2,
  })

  // Prowlarr TV search query
  const { data: tvResults, isLoading: tvSearching } = useQuery({
    queryKey: ['prowlarr-tv', tvSearchTerm],
    queryFn: () => importer.searchProwlarrTv(tvSearchTerm),
    enabled: tvSearchTerm.length > 2,
  })

  // Filter and sort movie results
  const filteredMovieResults = useMemo(() => {
    if (!movieResults) return []
    let results = [...movieResults]
    // Filter for 1080p if enabled
    if (movieFilter1080p) {
      results = results.filter(r => r.title.toLowerCase().includes('1080p'))
    }
    // Sort by seeders descending
    results.sort((a, b) => b.seeders - a.seeders)
    return results
  }, [movieResults, movieFilter1080p])

  // Filter and sort TV results
  const filteredTvResults = useMemo(() => {
    if (!tvResults) return []
    let results = [...tvResults]
    // Filter for 1080p if enabled
    if (tvFilter1080p) {
      results = results.filter(r => r.title.toLowerCase().includes('1080p'))
    }
    // Sort by seeders descending
    results.sort((a, b) => b.seeders - a.seeders)
    return results
  }, [tvResults, tvFilter1080p])

  const handleMovieSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (movieQuery.trim().length > 2) {
      setMovieSearchTerm(movieQuery.trim())
      setMovieDownloadingGuids(new Set())
      setMovieDownloadedGuids(new Set())
      setMovieDownloadErrors({})
    }
  }

  const handleTvSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (tvQuery.trim().length > 2) {
      setTvSearchTerm(tvQuery.trim())
      setTvDownloadingGuids(new Set())
      setTvDownloadedGuids(new Set())
      setTvDownloadErrors({})
    }
  }

  // Prowlarr movie download mutation
  const downloadMovieMutation = useMutation({
    mutationFn: async (result: ProwlarrVideoResult) => {
      return importer.downloadProwlarr(
        result.guid,
        result.indexer_id,
        result.title,
        'movie',
        result.download_url,
        result.magnet_url
      )
    },
    onMutate: (result) => {
      setMovieDownloadingGuids(prev => new Set(prev).add(result.guid))
      setMovieDownloadErrors(prev => {
        const next = { ...prev }
        delete next[result.guid]
        return next
      })
    },
    onSuccess: (data, result) => {
      setMovieDownloadingGuids(prev => {
        const next = new Set(prev)
        next.delete(result.guid)
        return next
      })
      if (data.success) {
        setMovieDownloadedGuids(prev => new Set(prev).add(result.guid))
      } else {
        setMovieDownloadErrors(prev => ({
          ...prev,
          [result.guid]: data.message || 'Download failed'
        }))
      }
    },
    onError: (error: unknown, result) => {
      setMovieDownloadingGuids(prev => {
        const next = new Set(prev)
        next.delete(result.guid)
        return next
      })
      let errorMsg = 'Download failed'
      if (error && typeof error === 'object') {
        const axiosError = error as { response?: { data?: string }; message?: string }
        errorMsg = axiosError.response?.data || axiosError.message || 'Download failed'
      }
      setMovieDownloadErrors(prev => ({
        ...prev,
        [result.guid]: errorMsg
      }))
    },
  })

  // Prowlarr TV download mutation
  const downloadTvMutation = useMutation({
    mutationFn: async (result: ProwlarrVideoResult) => {
      return importer.downloadProwlarr(
        result.guid,
        result.indexer_id,
        result.title,
        'tv',
        result.download_url,
        result.magnet_url
      )
    },
    onMutate: (result) => {
      setTvDownloadingGuids(prev => new Set(prev).add(result.guid))
      setTvDownloadErrors(prev => {
        const next = { ...prev }
        delete next[result.guid]
        return next
      })
    },
    onSuccess: (data, result) => {
      setTvDownloadingGuids(prev => {
        const next = new Set(prev)
        next.delete(result.guid)
        return next
      })
      if (data.success) {
        setTvDownloadedGuids(prev => new Set(prev).add(result.guid))
      } else {
        setTvDownloadErrors(prev => ({
          ...prev,
          [result.guid]: data.message || 'Download failed'
        }))
      }
    },
    onError: (error: unknown, result) => {
      setTvDownloadingGuids(prev => {
        const next = new Set(prev)
        next.delete(result.guid)
        return next
      })
      let errorMsg = 'Download failed'
      if (error && typeof error === 'object') {
        const axiosError = error as { response?: { data?: string }; message?: string }
        errorMsg = axiosError.response?.data || axiosError.message || 'Download failed'
      }
      setTvDownloadErrors(prev => ({
        ...prev,
        [result.guid]: errorMsg
      }))
    },
  })

  // Helper to format file size
  const formatSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
  }

  // Helper to format duration
  const formatDuration = (seconds: number | null) => {
    if (!seconds) return null
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    if (mins >= 60) {
      const hrs = Math.floor(mins / 60)
      const remainingMins = mins % 60
      return `${hrs}h ${remainingMins}m`
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Format sample rate for display (handles both Hz and kHz values)
  const formatSampleRate = (rate: number) => {
    return rate >= 1000 ? (rate / 1000).toFixed(1) : rate.toFixed(1)
  }

  // Render an album card for search results (shared across DAB/Monochrome/Squid)
  const renderAlbumCard = (
    result: DabSearchResult | HifiSearchResult,
    downloadingIds: Set<string>,
    downloadedIds: Set<string>,
    downloadErrors: Record<string, string>,
    onDownload: (result: DabSearchResult | HifiSearchResult) => void,
  ) => (
    <div
      key={result.album_id}
      className="bg-dark-800 rounded-xl p-5 flex gap-5"
    >
      {/* Album cover */}
      <div className="w-36 h-36 flex-shrink-0 bg-dark-700 rounded-lg overflow-hidden shadow-lg">
        {result.cover_url ? (
          <img
            src={result.cover_url}
            alt={result.title}
            className="w-full h-full object-cover" loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Disc3 size={48} className="text-dark-500" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Title and Artist */}
        <h3 className="font-semibold text-lg truncate" title={result.title}>
          {result.title}
        </h3>
        <p className="text-dark-300 truncate" title={result.artist}>
          {result.artist}
        </p>

        {/* Metadata grid */}
        <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <div className="flex items-center gap-2 text-dark-400">
            <span className="text-dark-500">Tracks:</span>
            <span>{result.track_count}</span>
          </div>
          {result.year && (
            <div className="flex items-center gap-2 text-dark-400">
              <span className="text-dark-500">Year:</span>
              <span>{result.year}</span>
            </div>
          )}
          {result.genre && (
            <div className="flex items-center gap-2 text-dark-400">
              <span className="text-dark-500">Genre:</span>
              <span className="truncate">{result.genre}</span>
            </div>
          )}
          {result.label && (
            <div className="flex items-center gap-2 text-dark-400">
              <span className="text-dark-500">Label:</span>
              <span className="truncate">{result.label}</span>
            </div>
          )}
          {result.duration && (
            <div className="flex items-center gap-2 text-dark-400">
              <span className="text-dark-500">Duration:</span>
              <span>{formatDuration(result.duration)}</span>
            </div>
          )}
        </div>

        {/* Quality badges */}
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <span className="bg-dark-700 text-dark-300 px-2 py-1 rounded text-xs">
            {result.bit_depth}bit / {formatSampleRate(result.sample_rate)}kHz
          </span>
          {result.is_hi_res && (
            <span className="bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded text-xs font-medium">
              Hi-Res Audio
            </span>
          )}
          <span className="bg-blue-500/20 text-blue-400 px-2 py-1 rounded text-xs">
            FLAC
          </span>
        </div>

        {/* Download button */}
        <div className="mt-auto pt-3">
          {(() => {
            const existingId = getExistingAlbumId(result)
            if (existingId) {
              return (
                <Link
                  to={`/albums/${existingId}`}
                  className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300"
                >
                  <CheckCircle size={18} />
                  <span className="text-sm font-medium">Already in Library</span>
                  <ChevronRight size={14} />
                </Link>
              )
            }
            if (downloadedIds.has(result.album_id)) {
              return (
                <div className="flex items-center gap-2 text-green-400">
                  <CheckCircle size={18} />
                  <span className="text-sm font-medium">Downloaded & Added to Library</span>
                </div>
              )
            }
            if (downloadErrors[result.album_id]) {
              const errorMsg = downloadErrors[result.album_id]
              const isConfigError = errorMsg.includes('upload target') || errorMsg.includes('not configured')
              return (
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-red-400">
                    <AlertCircle size={18} />
                    <span className="text-sm">
                      {isConfigError ? 'Configuration Required' : 'Download Failed'}
                    </span>
                  </div>
                  <p className="text-xs text-dark-400 max-w-xs">
                    {errorMsg}
                  </p>
                  {isConfigError ? (
                    <Link
                      to="/libraries"
                      className="inline-flex items-center gap-1 text-xs text-primary-400 hover:text-primary-300"
                    >
                      Go to Settings
                      <ChevronRight size={12} />
                    </Link>
                  ) : (
                    <button
                      onClick={() => onDownload(result)}
                      className="text-xs text-primary-400 hover:text-primary-300"
                    >
                      Retry
                    </button>
                  )}
                </div>
              )
            }
            return (
              <button
                onClick={() => onDownload(result)}
                disabled={downloadingIds.has(result.album_id)}
                className="btn btn-primary flex items-center gap-2"
              >
                {downloadingIds.has(result.album_id) ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download size={16} />
                    Download Album
                  </>
                )}
              </button>
            )
          })()}
        </div>
      </div>
    </div>
  )

  // YouTube Search state
  const [ytQuery, setYtQuery] = useState('')
  const [ytSearchTerm, setYtSearchTerm] = useState('')
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set())
  const [downloadedIds, setDownloadedIds] = useState<Set<string>>(new Set())
  const [downloadErrors, setDownloadErrors] = useState<Record<string, string>>({})
  const queryClient = useQueryClient()

  // YouTube search query
  const { data: ytResults, isLoading: ytSearching } = useQuery({
    queryKey: ['youtube-search', ytSearchTerm],
    queryFn: () => importer.searchYoutube(ytSearchTerm, 10),
    enabled: ytSearchTerm.length > 2,
  })

  const handleYtSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (ytQuery.trim().length > 2) {
      setYtSearchTerm(ytQuery.trim())
      // Clear previous download states
      setDownloadingIds(new Set())
      setDownloadedIds(new Set())
      setDownloadErrors({})
    }
  }

  // YouTube download mutation
  const downloadYtMutation = useMutation({
    mutationFn: async (result: YtSearchResult) => {
      // Parse artist and title from the video title
      // Common format: "Artist - Title" or just "Title"
      const parts = result.title.split(' - ')
      let artist = result.channel
      let title = result.title

      if (parts.length >= 2) {
        artist = parts[0].trim()
        title = parts.slice(1).join(' - ').trim()
      }

      return importer.downloadYoutubeStandalone(result.url, artist, title)
    },
    onMutate: (result) => {
      setDownloadingIds(prev => new Set(prev).add(result.id))
      setDownloadErrors(prev => {
        const next = { ...prev }
        delete next[result.id]
        return next
      })
    },
    onSuccess: (data, result) => {
      setDownloadingIds(prev => {
        const next = new Set(prev)
        next.delete(result.id)
        return next
      })
      if (data.success) {
        setDownloadedIds(prev => new Set(prev).add(result.id))
        // Invalidate library queries to show new tracks
        queryClient.invalidateQueries({ queryKey: ['tracks'] })
        queryClient.invalidateQueries({ queryKey: ['artists'] })
        queryClient.invalidateQueries({ queryKey: ['albums'] })
      } else {
        setDownloadErrors(prev => ({
          ...prev,
          [result.id]: data.error || 'Download failed'
        }))
      }
    },
    onError: (error: Error, result) => {
      setDownloadingIds(prev => {
        const next = new Set(prev)
        next.delete(result.id)
        return next
      })
      setDownloadErrors(prev => ({
        ...prev,
        [result.id]: error.message
      }))
    },
  })

  const tabs = [
    { id: 'music' as MediaTab, label: 'Music', icon: Music },
    { id: 'movies' as MediaTab, label: 'Movies', icon: Film },
    { id: 'tv' as MediaTab, label: 'TV Shows', icon: Tv },
    { id: 'find-music' as MediaTab, label: 'Find New Music', icon: Sparkles },
    { id: 'youtube' as MediaTab, label: 'YouTube', icon: Youtube },
  ]

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-primary-600/20 rounded-xl flex items-center justify-center">
          <Compass size={24} className="text-primary-400" />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Discover</h1>
          <p className="text-dark-400 text-sm">Browse global catalog and shared content</p>
        </div>
      </div>

      {/* Source Filter */}
      <div className="flex gap-2 mb-6 flex-wrap">
        <button
          onClick={() => setSourceFilter('all')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
            sourceFilter === 'all'
              ? 'bg-primary-600 text-white'
              : 'bg-dark-800 text-dark-300 hover:bg-dark-700'
          }`}
        >
          <Compass size={18} />
          <span>All</span>
          <span className="text-xs opacity-70">({globalCount + sharedCount})</span>
        </button>
        <button
          onClick={() => setSourceFilter('global')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
            sourceFilter === 'global'
              ? 'bg-blue-600 text-white'
              : 'bg-dark-800 text-dark-300 hover:bg-dark-700'
          }`}
        >
          <Globe size={18} />
          <span>Global Catalog</span>
          <span className="text-xs opacity-70">({globalCount})</span>
        </button>
        <button
          onClick={() => setSourceFilter('shared')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
            sourceFilter === 'shared'
              ? 'bg-green-600 text-white'
              : 'bg-dark-800 text-dark-300 hover:bg-dark-700'
          }`}
        >
          <Users size={18} />
          <span>Shared With Me</span>
          <span className="text-xs opacity-70">({sharedCount})</span>
        </button>
      </div>

      {/* Media Type Tabs */}
      <div className="flex gap-1 mb-6 border-b border-dark-800">
        {tabs.map((tab) => {
          const TabIcon = tab.icon
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-400'
                  : 'border-transparent text-dark-400 hover:text-dark-200'
              }`}
            >
              <TabIcon size={18} />
              <span>{tab.label}</span>
            </button>
          )
        })}
      </div>

      {/* Content */}
      {activeTab === 'music' && (
        <div className="space-y-8">
          {/* Artists Section */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Artists ({filteredArtists.length})</h2>
              {filteredArtists.length > 12 && (
                <button
                  onClick={() => setShowAllArtists(!showAllArtists)}
                  className="text-primary-400 hover:text-primary-300 text-sm flex items-center gap-1"
                >
                  {showAllArtists ? 'Show Less' : 'View All'} <ChevronRight size={16} className={showAllArtists ? 'rotate-90' : ''} />
                </button>
              )}
            </div>
            {artistsLoading || albumsLoading ? (
              <div className="text-dark-400">Loading artists...</div>
            ) : filteredArtists.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {(showAllArtists ? filteredArtists : filteredArtists.slice(0, 12)).map((artist) => (
                  <Link
                    key={artist.id}
                    to={`/artists/${artist.id}`}
                    className="group"
                  >
                    <div className="aspect-square bg-dark-800 rounded-full overflow-hidden mb-2 flex items-center justify-center">
                      {artist.image_path ? (
                        <img
                          src={`/api/image/${encodeURIComponent(artist.image_path)}`}
                          alt={artist.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                      ) : (
                        <User size={32} className="text-dark-600" />
                      )}
                    </div>
                    <p className="font-medium truncate text-center group-hover:text-primary-400 transition-colors">
                      {artist.name}
                    </p>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-dark-500">No artists found</div>
            )}
          </section>

          {/* Albums Section */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Albums ({filteredAlbums.length})</h2>
              {filteredAlbums.length > 12 && (
                <button
                  onClick={() => setShowAllAlbums(!showAllAlbums)}
                  className="text-primary-400 hover:text-primary-300 text-sm flex items-center gap-1"
                >
                  {showAllAlbums ? 'Show Less' : 'View All'} <ChevronRight size={16} className={showAllAlbums ? 'rotate-90' : ''} />
                </button>
              )}
            </div>
            {albumsLoading ? (
              <div className="text-dark-400">Loading albums...</div>
            ) : filteredAlbums.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {(showAllAlbums ? filteredAlbums : filteredAlbums.slice(0, 12)).map((album) => (
                  <Link
                    key={album.id}
                    to={`/albums/${album.id}`}
                    className="group"
                  >
                    <div className="aspect-square bg-dark-800 rounded-lg overflow-hidden mb-2">
                      {album.cover_path ? (
                        <img
                          src={`/api/image/${encodeURIComponent(album.cover_path)}`}
                          alt={album.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Music size={32} className="text-dark-600" />
                        </div>
                      )}
                    </div>
                    <p className="font-medium truncate group-hover:text-primary-400 transition-colors">
                      {album.title}
                    </p>
                    <p className="text-sm text-dark-400 truncate">{album.artist_name || 'Unknown Artist'}</p>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-dark-500">No albums found</div>
            )}
          </section>
        </div>
      )}

      {activeTab === 'movies' && (
        <section>
          {/* Downloads in Progress */}
          {movieSyncStatus && (movieSyncStatus.completed.length > 0 || movieSyncStatus.syncing.length > 0 || movieSyncStatus.synced.length > 0) && (
            <div className="mb-8 bg-dark-800 rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <HardDrive size={20} className="text-primary-400" />
                  Downloads
                </h3>
                <button
                  onClick={() => refetchVideoSync()}
                  className="text-dark-400 hover:text-dark-200 p-1"
                  title="Refresh status"
                >
                  <RefreshCw size={16} />
                </button>
              </div>

              <div className="space-y-3">
                {/* Completed - ready to sync */}
                {movieSyncStatus.completed.filter(t => !t.is_synced).map((torrent) => (
                  <div key={torrent.hash} className="flex items-center gap-4 p-3 bg-dark-700/50 rounded-lg">
                    <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <CheckCircle size={20} className="text-green-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{torrent.name}</p>
                      <p className="text-sm text-green-400">Downloaded - Ready to sync</p>
                    </div>
                    <button
                      onClick={() => syncVideoMutation.mutate({ label: 'movies', hash: torrent.hash })}
                      disabled={syncingHash !== null}
                      className="btn btn-primary btn-sm flex items-center gap-2"
                    >
                      {syncingHash === torrent.hash ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <RefreshCw size={14} />
                      )}
                      {syncingHash === torrent.hash ? 'Syncing...' : 'Sync to Library'}
                    </button>
                  </div>
                ))}

                {/* Currently syncing */}
                {movieSyncStatus.syncing.map((torrent) => (
                  <div key={torrent.hash} className="flex items-center gap-4 p-3 bg-dark-700/50 rounded-lg">
                    <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Loader2 size={20} className="text-blue-400 animate-spin" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{torrent.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex-1 h-2 bg-dark-600 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-500 transition-all duration-300"
                            style={{ width: `${torrent.progress}%` }}
                          />
                        </div>
                        <span className="text-sm text-blue-400 w-12 text-right">{torrent.progress}%</span>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Already synced - collapsible */}
                {movieSyncStatus.synced.length > 0 && (
                  <div className="border-t border-dark-600 pt-3 mt-3">
                    <button
                      onClick={() => setShowSyncedMovies(!showSyncedMovies)}
                      className="flex items-center gap-2 text-dark-400 hover:text-dark-200 text-sm mb-2"
                    >
                      {showSyncedMovies ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      <span>{movieSyncStatus.synced.length} synced to library</span>
                    </button>
                    {showSyncedMovies && (
                      <div className="space-y-2">
                        {movieSyncStatus.synced.map((name) => (
                          <div key={name} className="flex items-center gap-3 p-2 bg-dark-700/30 rounded-lg opacity-60">
                            <CheckCircle size={16} className="text-dark-500 flex-shrink-0" />
                            <p className="text-sm truncate">{name}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Sync all button */}
              {movieSyncStatus.completed.filter(t => !t.is_synced).length > 1 && (
                <button
                  onClick={() => syncVideoMutation.mutate({ label: 'movies' })}
                  disabled={syncingHash !== null}
                  className="mt-4 w-full btn btn-primary flex items-center justify-center gap-2"
                >
                  {syncingHash === 'all' ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <RefreshCw size={16} />
                  )}
                  {syncingHash === 'all' ? 'Syncing All...' : `Sync All (${movieSyncStatus.completed.filter(t => !t.is_synced).length} movies)`}
                </button>
              )}
            </div>
          )}

          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-2">Find Movies</h2>
            <p className="text-dark-400 text-sm">Search for movies to download via Prowlarr</p>
          </div>

          {/* Search Form */}
          <form onSubmit={handleMovieSearch} className="mb-4">
            <div className="flex gap-2 max-w-xl">
              <div className="relative flex-1">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-400" />
                <input
                  type="text"
                  value={movieQuery}
                  onChange={(e) => setMovieQuery(e.target.value)}
                  placeholder="Search for movies..."
                  className="w-full pl-10 pr-4 py-2.5 bg-dark-800 border border-dark-700 rounded-lg focus:outline-none focus:border-primary-500 text-dark-100"
                />
              </div>
              <button
                type="submit"
                disabled={movieQuery.trim().length < 3 || movieSearching}
                className="btn btn-primary flex items-center gap-2"
              >
                {movieSearching ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
                Search
              </button>
            </div>
          </form>

          {/* Filter Options */}
          <div className="mb-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={movieFilter1080p}
                onChange={(e) => setMovieFilter1080p(e.target.checked)}
                className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-primary-500 focus:ring-offset-0"
              />
              <span className="text-sm text-dark-300">1080p only</span>
            </label>
          </div>

          {/* Search Results */}
          {movieSearching && (
            <div className="text-center py-12">
              <Loader2 size={32} className="mx-auto mb-4 animate-spin text-primary-400" />
              <p className="text-dark-400">Searching for movies...</p>
            </div>
          )}

          {!movieSearching && filteredMovieResults.length > 0 && (
            <div className="space-y-3">
              {filteredMovieResults.map((result) => (
                <div
                  key={result.guid}
                  className="bg-dark-800 rounded-lg p-4 flex gap-4"
                >
                  <div className="w-16 h-24 flex-shrink-0 bg-dark-700 rounded overflow-hidden flex items-center justify-center">
                    <Film size={32} className="text-dark-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium line-clamp-2" title={result.title}>
                      {result.title}
                    </h3>
                    <div className="flex items-center gap-3 mt-2 text-sm text-dark-400">
                      <span>{formatSize(result.size)}</span>
                      <span>•</span>
                      <span>{result.seeders} seeders</span>
                      <span>•</span>
                      <span className="text-dark-500">{result.indexer_name}</span>
                    </div>
                  </div>
                  <div className="flex items-center">
                    {movieDownloadedGuids.has(result.guid) ? (
                      <div className="flex items-center gap-2 text-green-400">
                        <CheckCircle size={20} />
                        <span className="text-sm">Started</span>
                      </div>
                    ) : movieDownloadErrors[result.guid] ? (
                      <div className="flex items-center gap-2 text-red-400" title={movieDownloadErrors[result.guid]}>
                        <AlertCircle size={20} />
                        <span className="text-sm">Failed</span>
                      </div>
                    ) : (
                      <button
                        onClick={() => downloadMovieMutation.mutate(result)}
                        disabled={movieDownloadingGuids.has(result.guid)}
                        className="btn btn-primary flex items-center gap-2"
                      >
                        {movieDownloadingGuids.has(result.guid) ? (
                          <>
                            <Loader2 size={16} className="animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Download size={16} />
                            Download
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {!movieSearching && movieSearchTerm && filteredMovieResults.length === 0 && (
            <div className="text-center py-12">
              <Search size={48} className="mx-auto mb-4 text-dark-600" />
              <p className="text-dark-400">No results found for "{movieSearchTerm}"</p>
              <p className="text-dark-500 text-sm mt-2">
                {movieFilter1080p && movieResults && movieResults.length > 0
                  ? 'Try unchecking the 1080p filter'
                  : 'Try a different search term'}
              </p>
            </div>
          )}

          {!movieSearching && !movieSearchTerm && (
            <div className="text-center py-12">
              <Film size={48} className="mx-auto mb-4 text-dark-600" />
              <p className="text-dark-400">Search for movies to download</p>
              <p className="text-dark-500 text-sm mt-2">Results are sent to your configured download client</p>
            </div>
          )}
        </section>
      )}

      {activeTab === 'tv' && (
        <section>
          {/* Downloads in Progress */}
          {tvSyncStatus && (tvSyncStatus.completed.length > 0 || tvSyncStatus.syncing.length > 0 || tvSyncStatus.synced.length > 0) && (
            <div className="mb-8 bg-dark-800 rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <HardDrive size={20} className="text-primary-400" />
                  Downloads
                </h3>
                <button
                  onClick={() => refetchVideoSync()}
                  className="text-dark-400 hover:text-dark-200 p-1"
                  title="Refresh status"
                >
                  <RefreshCw size={16} />
                </button>
              </div>

              <div className="space-y-3">
                {/* Completed - ready to sync */}
                {tvSyncStatus.completed.filter(t => !t.is_synced).map((torrent) => (
                  <div key={torrent.hash} className="flex items-center gap-4 p-3 bg-dark-700/50 rounded-lg">
                    <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <CheckCircle size={20} className="text-green-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{torrent.name}</p>
                      <p className="text-sm text-green-400">Downloaded - Ready to sync</p>
                    </div>
                    <button
                      onClick={() => syncVideoMutation.mutate({ label: 'tv', hash: torrent.hash })}
                      disabled={syncingHash !== null}
                      className="btn btn-primary btn-sm flex items-center gap-2"
                    >
                      {syncingHash === torrent.hash ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <RefreshCw size={14} />
                      )}
                      {syncingHash === torrent.hash ? 'Syncing...' : 'Sync to Library'}
                    </button>
                  </div>
                ))}

                {/* Currently syncing */}
                {tvSyncStatus.syncing.map((torrent) => (
                  <div key={torrent.hash} className="flex items-center gap-4 p-3 bg-dark-700/50 rounded-lg">
                    <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Loader2 size={20} className="text-blue-400 animate-spin" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{torrent.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex-1 h-2 bg-dark-600 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-500 transition-all duration-300"
                            style={{ width: `${torrent.progress}%` }}
                          />
                        </div>
                        <span className="text-sm text-blue-400 w-12 text-right">{torrent.progress}%</span>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Already synced - collapsible */}
                {tvSyncStatus.synced.length > 0 && (
                  <div className="border-t border-dark-600 pt-3 mt-3">
                    <button
                      onClick={() => setShowSyncedTv(!showSyncedTv)}
                      className="flex items-center gap-2 text-dark-400 hover:text-dark-200 text-sm mb-2"
                    >
                      {showSyncedTv ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      <span>{tvSyncStatus.synced.length} synced to library</span>
                    </button>
                    {showSyncedTv && (
                      <div className="space-y-2">
                        {tvSyncStatus.synced.map((name) => (
                          <div key={name} className="flex items-center gap-3 p-2 bg-dark-700/30 rounded-lg opacity-60">
                            <CheckCircle size={16} className="text-dark-500 flex-shrink-0" />
                            <p className="text-sm truncate">{name}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Sync all button */}
              {tvSyncStatus.completed.filter(t => !t.is_synced).length > 1 && (
                <button
                  onClick={() => syncVideoMutation.mutate({ label: 'tv' })}
                  disabled={syncingHash !== null}
                  className="mt-4 w-full btn btn-primary flex items-center justify-center gap-2"
                >
                  {syncingHash === 'all' ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <RefreshCw size={16} />
                  )}
                  {syncingHash === 'all' ? 'Syncing All...' : `Sync All (${tvSyncStatus.completed.filter(t => !t.is_synced).length} shows)`}
                </button>
              )}
            </div>
          )}

          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-2">Find TV Shows</h2>
            <p className="text-dark-400 text-sm">Search for TV shows to download via Prowlarr</p>
          </div>

          {/* Search Form */}
          <form onSubmit={handleTvSearch} className="mb-4">
            <div className="flex gap-2 max-w-xl">
              <div className="relative flex-1">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-400" />
                <input
                  type="text"
                  value={tvQuery}
                  onChange={(e) => setTvQuery(e.target.value)}
                  placeholder="Search for TV shows..."
                  className="w-full pl-10 pr-4 py-2.5 bg-dark-800 border border-dark-700 rounded-lg focus:outline-none focus:border-primary-500 text-dark-100"
                />
              </div>
              <button
                type="submit"
                disabled={tvQuery.trim().length < 3 || tvSearching}
                className="btn btn-primary flex items-center gap-2"
              >
                {tvSearching ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
                Search
              </button>
            </div>
          </form>

          {/* Filter Options */}
          <div className="mb-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={tvFilter1080p}
                onChange={(e) => setTvFilter1080p(e.target.checked)}
                className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-primary-500 focus:ring-offset-0"
              />
              <span className="text-sm text-dark-300">1080p only</span>
            </label>
          </div>

          {/* Search Results */}
          {tvSearching && (
            <div className="text-center py-12">
              <Loader2 size={32} className="mx-auto mb-4 animate-spin text-primary-400" />
              <p className="text-dark-400">Searching for TV shows...</p>
            </div>
          )}

          {!tvSearching && filteredTvResults.length > 0 && (
            <div className="space-y-3">
              {filteredTvResults.map((result) => (
                <div
                  key={result.guid}
                  className="bg-dark-800 rounded-lg p-4 flex gap-4"
                >
                  <div className="w-16 h-24 flex-shrink-0 bg-dark-700 rounded overflow-hidden flex items-center justify-center">
                    <Tv size={32} className="text-dark-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium line-clamp-2" title={result.title}>
                      {result.title}
                    </h3>
                    <div className="flex items-center gap-3 mt-2 text-sm text-dark-400">
                      <span>{formatSize(result.size)}</span>
                      <span>•</span>
                      <span>{result.seeders} seeders</span>
                      <span>•</span>
                      <span className="text-dark-500">{result.indexer_name}</span>
                    </div>
                  </div>
                  <div className="flex items-center">
                    {tvDownloadedGuids.has(result.guid) ? (
                      <div className="flex items-center gap-2 text-green-400">
                        <CheckCircle size={20} />
                        <span className="text-sm">Started</span>
                      </div>
                    ) : tvDownloadErrors[result.guid] ? (
                      <div className="flex items-center gap-2 text-red-400" title={tvDownloadErrors[result.guid]}>
                        <AlertCircle size={20} />
                        <span className="text-sm">Failed</span>
                      </div>
                    ) : (
                      <button
                        onClick={() => downloadTvMutation.mutate(result)}
                        disabled={tvDownloadingGuids.has(result.guid)}
                        className="btn btn-primary flex items-center gap-2"
                      >
                        {tvDownloadingGuids.has(result.guid) ? (
                          <>
                            <Loader2 size={16} className="animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Download size={16} />
                            Download
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {!tvSearching && tvSearchTerm && filteredTvResults.length === 0 && (
            <div className="text-center py-12">
              <Search size={48} className="mx-auto mb-4 text-dark-600" />
              <p className="text-dark-400">No results found for "{tvSearchTerm}"</p>
              <p className="text-dark-500 text-sm mt-2">
                {tvFilter1080p && tvResults && tvResults.length > 0
                  ? 'Try unchecking the 1080p filter'
                  : 'Try a different search term'}
              </p>
            </div>
          )}

          {!tvSearching && !tvSearchTerm && (
            <div className="text-center py-12">
              <Tv size={48} className="mx-auto mb-4 text-dark-600" />
              <p className="text-dark-400">Search for TV shows to download</p>
              <p className="text-dark-500 text-sm mt-2">Results are sent to your configured download client</p>
            </div>
          )}
        </section>
      )}

      {activeTab === 'find-music' && (
        <section>
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-2">Find New Music</h2>
            <p className="text-dark-400 text-sm">Search for high-quality music to add to your library</p>
          </div>

          {/* Source sub-tabs */}
          <div className="flex gap-2 mb-6">
            {([
              { id: 'dab' as MusicSubTab, label: 'DAB' },
              { id: 'monochrome' as MusicSubTab, label: 'Monochrome' },
              { id: 'squid' as MusicSubTab, label: 'Squid' },
            ]).map(tab => (
              <button
                key={tab.id}
                onClick={() => setMusicSubTab(tab.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  musicSubTab === tab.id
                    ? 'bg-primary-600 text-white'
                    : 'bg-dark-800 text-dark-300 hover:bg-dark-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* DAB sub-tab */}
          {musicSubTab === 'dab' && (
            <>
              <form onSubmit={handleDabSearch} className="mb-6">
                <div className="flex gap-2 max-w-xl">
                  <div className="relative flex-1">
                    <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-400" />
                    <input
                      type="text"
                      value={dabQuery}
                      onChange={(e) => setDabQuery(e.target.value)}
                      placeholder="Search artist or album..."
                      className="w-full pl-10 pr-4 py-2.5 bg-dark-800 border border-dark-700 rounded-lg focus:outline-none focus:border-primary-500 text-dark-100"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={dabQuery.trim().length < 3 || dabSearching}
                    className="btn btn-primary flex items-center gap-2"
                  >
                    {dabSearching ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
                    Search
                  </button>
                </div>
              </form>

              {dabSearching && (
                <div className="text-center py-12">
                  <Loader2 size={32} className="mx-auto mb-4 animate-spin text-primary-400" />
                  <p className="text-dark-400">Searching DAB...</p>
                </div>
              )}

              {!dabSearching && dabResults && dabResults.length > 0 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {dabResults.map((result: DabSearchResult) =>
                    renderAlbumCard(result, dabDownloadingIds, dabDownloadedIds, dabDownloadErrors, (r) => downloadDabMutation.mutate(r as DabSearchResult))
                  )}
                </div>
              )}

              {!dabSearching && dabResults && dabResults.length === 0 && dabSearchTerm && (
                <div className="text-center py-12">
                  <Search size={48} className="mx-auto mb-4 text-dark-600" />
                  <p className="text-dark-400">No results found for "{dabSearchTerm}"</p>
                  <p className="text-dark-500 text-sm mt-2">Try a different search term</p>
                </div>
              )}

              {!dabSearching && !dabSearchTerm && (
                <div className="text-center py-12">
                  <Sparkles size={48} className="mx-auto mb-4 text-dark-600" />
                  <p className="text-dark-400">Search for artists or albums to discover new music</p>
                  <p className="text-dark-500 text-sm mt-2">Results include high-quality FLAC downloads</p>
                </div>
              )}
            </>
          )}

          {/* Monochrome sub-tab */}
          {musicSubTab === 'monochrome' && (
            <>
              <form onSubmit={handleMonoSearch} className="mb-6">
                <div className="flex gap-2 max-w-xl">
                  <div className="relative flex-1">
                    <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-400" />
                    <input
                      type="text"
                      value={monoQuery}
                      onChange={(e) => setMonoQuery(e.target.value)}
                      placeholder="Search artist or album..."
                      className="w-full pl-10 pr-4 py-2.5 bg-dark-800 border border-dark-700 rounded-lg focus:outline-none focus:border-primary-500 text-dark-100"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={monoQuery.trim().length < 3 || monoSearching}
                    className="btn btn-primary flex items-center gap-2"
                  >
                    {monoSearching ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
                    Search
                  </button>
                </div>
              </form>

              {monoSearching && (
                <div className="text-center py-12">
                  <Loader2 size={32} className="mx-auto mb-4 animate-spin text-primary-400" />
                  <p className="text-dark-400">Searching Monochrome...</p>
                </div>
              )}

              {!monoSearching && monoResults && monoResults.length > 0 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {monoResults.map((result: HifiSearchResult) =>
                    renderAlbumCard(result, monoDownloadingIds, monoDownloadedIds, monoDownloadErrors, (r) => downloadMonoMutation.mutate(r as HifiSearchResult))
                  )}
                </div>
              )}

              {!monoSearching && monoResults && monoResults.length === 0 && monoSearchTerm && (
                <div className="text-center py-12">
                  <Search size={48} className="mx-auto mb-4 text-dark-600" />
                  <p className="text-dark-400">No results found for "{monoSearchTerm}"</p>
                  <p className="text-dark-500 text-sm mt-2">Try a different search term</p>
                </div>
              )}

              {!monoSearching && !monoSearchTerm && (
                <div className="text-center py-12">
                  <Sparkles size={48} className="mx-auto mb-4 text-dark-600" />
                  <p className="text-dark-400">Search for artists or albums on Monochrome</p>
                  <p className="text-dark-500 text-sm mt-2">High-quality Tidal downloads via Monochrome</p>
                </div>
              )}
            </>
          )}

          {/* Squid sub-tab */}
          {musicSubTab === 'squid' && (
            <>
              <form onSubmit={handleSquidSearch} className="mb-6">
                <div className="flex gap-2 max-w-xl">
                  <div className="relative flex-1">
                    <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-400" />
                    <input
                      type="text"
                      value={squidQuery}
                      onChange={(e) => setSquidQuery(e.target.value)}
                      placeholder="Search artist or album..."
                      className="w-full pl-10 pr-4 py-2.5 bg-dark-800 border border-dark-700 rounded-lg focus:outline-none focus:border-primary-500 text-dark-100"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={squidQuery.trim().length < 3 || squidSearching}
                    className="btn btn-primary flex items-center gap-2"
                  >
                    {squidSearching ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
                    Search
                  </button>
                </div>
              </form>

              {squidSearching && (
                <div className="text-center py-12">
                  <Loader2 size={32} className="mx-auto mb-4 animate-spin text-primary-400" />
                  <p className="text-dark-400">Searching Squid...</p>
                </div>
              )}

              {!squidSearching && squidResults && squidResults.length > 0 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {squidResults.map((result: HifiSearchResult) =>
                    renderAlbumCard(result, squidDownloadingIds, squidDownloadedIds, squidDownloadErrors, (r) => downloadSquidMutation.mutate(r as HifiSearchResult))
                  )}
                </div>
              )}

              {!squidSearching && squidResults && squidResults.length === 0 && squidSearchTerm && (
                <div className="text-center py-12">
                  <Search size={48} className="mx-auto mb-4 text-dark-600" />
                  <p className="text-dark-400">No results found for "{squidSearchTerm}"</p>
                  <p className="text-dark-500 text-sm mt-2">Try a different search term</p>
                </div>
              )}

              {!squidSearching && !squidSearchTerm && (
                <div className="text-center py-12">
                  <Sparkles size={48} className="mx-auto mb-4 text-dark-600" />
                  <p className="text-dark-400">Search for artists or albums on Squid</p>
                  <p className="text-dark-500 text-sm mt-2">High-quality Tidal downloads via Squid</p>
                </div>
              )}
            </>
          )}
        </section>
      )}

      {activeTab === 'youtube' && (
        <section>
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-2">YouTube Music</h2>
            <p className="text-dark-400 text-sm">Search and download music from YouTube</p>
          </div>

          {/* Search Form */}
          <form onSubmit={handleYtSearch} className="mb-6">
            <div className="flex gap-2 max-w-xl">
              <div className="relative flex-1">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-400" />
                <input
                  type="text"
                  value={ytQuery}
                  onChange={(e) => setYtQuery(e.target.value)}
                  placeholder="Search for music on YouTube..."
                  className="w-full pl-10 pr-4 py-2.5 bg-dark-800 border border-dark-700 rounded-lg focus:outline-none focus:border-primary-500 text-dark-100"
                />
              </div>
              <button
                type="submit"
                disabled={ytQuery.trim().length < 3 || ytSearching}
                className="btn btn-primary flex items-center gap-2"
              >
                {ytSearching ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
                Search
              </button>
            </div>
          </form>

          {/* Search Results */}
          {ytSearching && (
            <div className="text-center py-12">
              <Loader2 size={32} className="mx-auto mb-4 animate-spin text-primary-400" />
              <p className="text-dark-400">Searching YouTube...</p>
            </div>
          )}

          {!ytSearching && ytResults && ytResults.length > 0 && (
            <div className="space-y-3">
              {ytResults.map((result) => (
                <div
                  key={result.id}
                  className="bg-dark-800 rounded-lg p-4 flex gap-4"
                >
                  <div className="w-32 h-20 flex-shrink-0 bg-dark-700 rounded overflow-hidden">
                    {result.thumbnail ? (
                      <img
                        src={result.thumbnail}
                        alt={result.title}
                        className="w-full h-full object-cover" loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Youtube size={32} className="text-dark-500" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium line-clamp-2" title={result.title}>
                      {result.title}
                    </h3>
                    <p className="text-sm text-dark-400 truncate mt-1" title={result.channel}>
                      {result.channel}
                    </p>
                    <div className="flex items-center gap-2 mt-1 text-xs text-dark-500">
                      <span>{result.duration}</span>
                    </div>
                  </div>
                  <div className="flex items-center">
                    {downloadedIds.has(result.id) ? (
                      <div className="flex items-center gap-2 text-green-400">
                        <CheckCircle size={20} />
                        <span className="text-sm">Downloaded</span>
                      </div>
                    ) : downloadErrors[result.id] ? (
                      <div className="flex items-center gap-2 text-red-400" title={downloadErrors[result.id]}>
                        <AlertCircle size={20} />
                        <span className="text-sm">Failed</span>
                      </div>
                    ) : (
                      <button
                        onClick={() => downloadYtMutation.mutate(result)}
                        disabled={downloadingIds.has(result.id)}
                        className="btn btn-primary flex items-center gap-2"
                      >
                        {downloadingIds.has(result.id) ? (
                          <>
                            <Loader2 size={16} className="animate-spin" />
                            Downloading...
                          </>
                        ) : (
                          <>
                            <Download size={16} />
                            Download
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {!ytSearching && ytResults && ytResults.length === 0 && ytSearchTerm && (
            <div className="text-center py-12">
              <Search size={48} className="mx-auto mb-4 text-dark-600" />
              <p className="text-dark-400">No results found for "{ytSearchTerm}"</p>
              <p className="text-dark-500 text-sm mt-2">Try a different search term</p>
            </div>
          )}

          {!ytSearching && !ytSearchTerm && (
            <div className="text-center py-12">
              <Youtube size={48} className="mx-auto mb-4 text-dark-600" />
              <p className="text-dark-400">Search for music videos on YouTube</p>
              <p className="text-dark-500 text-sm mt-2">Downloads are converted to high-quality FLAC</p>
            </div>
          )}
        </section>
      )}

      {/* Empty state when no discoverable libraries */}
      {globalCount === 0 && sharedCount === 0 && (
        <div className="text-center py-16">
          <Compass size={64} className="mx-auto mb-4 text-dark-600" />
          <h2 className="text-xl font-semibold mb-2">Nothing to Discover Yet</h2>
          <p className="text-dark-400 max-w-md mx-auto">
            There are no global libraries or shared content available.
            Ask an admin to make some libraries global, or have other users share their libraries with you.
          </p>
        </div>
      )}
    </div>
  )
}
